import { $inject, $type } from "../utils/di-container.js";
import { DomDetector } from "../utils/dom-detector.js";
import { appContext } from '../badge2.context.js';
import { badgeVariantAlias, badgeVariantInverseAlias, badgeVariants } from './badge-type.js';
import { LoggerService } from './logger.service.js';

/**
 * @typedef {object} _Placeholder
 * @property {_BadgeVariant} variant
 * @property {string} flavor
 * @property {HTMLElement} element
 */

/**
 * @typedef {function(placeholder: _Placeholder)} _PlaceholderService_ChangeCallback
 */

/**
 * Service used to locate badge2 placeholders, including ones added after initial boot finished.
 * It is also used to detect removed placeholders.
 */
export class PlaceholderService {

  /** @type {_Placeholder[]} */
  placeholders = [];

  /** @type {LoggerService} */
  _logger;

  /** @type {DomDetector} */
  _rootDomDetector;

  /** @type {_PlaceholderService_ChangeCallback[]} */
  _foundCallbacks = [];

  /** @type {_PlaceholderService_ChangeCallback[]} */
  _removedCallbacks = [];

  /** @type {DomDetectorQuery[]} */
  _placeholderQueries = [];

  /** @type {Object.<string, DomDetector>} */
  _detectorAttr = {};

  [$inject](obtain) {
    this._logger = obtain(LoggerService);
    this._rootDomDetector = obtain(DomDetector);
  }

  /**
   * Starts looking for placeholder changes
   */
  start() {
    // create a query for each badge variant possible/allowed (some include aliases)
    badgeVariants.forEach(variant => {
      const elementQuery = [ `div[data-authorizedby-badge="${variant}"]` ];
      if (badgeVariantAlias[variant]) {
        elementQuery.push(`div[data-authorizedby-badge="${badgeVariantAlias[variant]}"]`);
      }
      this._placeholderQueries.push(this._rootDomDetector.addQuery(elementQuery, element => {
        let placeholder = this.placeholders.find(p => p.variant === variant);
        if (element && !placeholder) {
          // found placeholder
          this._logger.info('placeholder-tag/added', 'Placeholder tag added: ' + variant, () => ({ variant }));

          const flavor = element.getAttribute('data-authorizedby-flavor');
          placeholder = { variant, flavor, element };
          this.placeholders.push(placeholder);
          this._foundCallbacks.forEach(cb => cb(placeholder));

          // add placeholder change checker (e.g. changing type without recreating the placeholder element)
          if (this._detectorAttr[variant]) {
            this._detectorAttr[variant].deactivate();
          }
          this._detectorAttr[variant] = new DomDetector({
            containerElement: element,
            checkEvents: {
              structureChange: false,
              attributeChange: ['data-authorizedby-badge']
            },
          });
          this._detectorAttr[variant].addCheck(() => {
            const displayVariant = element.getAttribute('data-authorizedby-badge');
            const flavor = element.getAttribute('data-authorizedby-flavor');
            const variant = badgeVariantInverseAlias[displayVariant] || displayVariant;
            if (variant !== placeholder.variant) {
              this._logger.info('placeholder-tag/changed', 'Placeholder tag changed: ' + variant, () => ({ newVariant: variant, oldVariant: placeholder.variant }));
              placeholder.variant = variant;
              placeholder.flavor = flavor;
              this._removedCallbacks.forEach(cb => cb(placeholder));
              this._foundCallbacks.forEach(cb => cb(placeholder));
            } else {
              this._logger.debug('placeholder/no-changes', 'Placeholder has no changes.', () => ({ variant }));
            }
          });
          appContext.applyInject(this._detectorAttr[placeholder.variant]);
          this._detectorAttr[placeholder.variant].activate(true);
        } else if (!element && placeholder) {
          // removed placeholder
          this._logger.info('placeholder-tag/removed', 'Placeholder tag removed: ' + variant);
          this.placeholders.splice(this.placeholders.indexOf(placeholder), 1);
          this._removedCallbacks.forEach(cb => cb(placeholder));
        }
      }));
    });

    this._rootDomDetector.checkNow();
  }

  /**
   * @param {_PlaceholderService_ChangeCallback} callback
   */
  onPlaceholderFound(callback) {
    this._foundCallbacks.push(callback);
  }

  /**
   * @param {_PlaceholderService_ChangeCallback} callback
   */
  onPlaceholderRemoved(callback) {
    this._removedCallbacks.push(callback);
  }

  /**
   * Stops looking for placeholder changes, and forgets about any found placeholders
   */
  stop() {
    this.placeholders = [];
    this._placeholderQueries.forEach(query => this._rootDomDetector.removeQuery(query));
    this._placeholderQueries = [];
    Object.values(this._detectorAttr).forEach(detector => detector.deactivate());
  }

}

PlaceholderService[$type] = Symbol('PlaceholderService');
